import React, { useState } from "react";
import axios from "axios";
import { Lock, ArrowRight, Download } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Enquiries = () => {
  const [passcode, setPasscode] = useState("");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const unlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/enquiries`, { params: { token: passcode } });
      setRows(res.data);
    } catch (err) {
      setError("Incorrect passcode. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (d) => {
    try {
      return new Date(d).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return d; }
  };

  const downloadCsv = () => {
    if (!rows || rows.length === 0) return;
    const headers = ["Received", "Name", "Occasion", "Email", "Phone", "Event Date", "Guests", "Message"];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      lines.push([
        fmt(r.created_at), r.name, r.occasion, r.email, r.phone, r.event_date, r.guests, r.message,
      ].map(esc).join(","));
    });
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tkac-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-ivory">
      <Header />
      <main className="flex-grow pt-40 pb-narrative-gap px-margin-mobile md:px-margin-desktop">
        {rows === null ? (
          <div className="max-w-md mx-auto text-center">
            <Lock className="w-6 h-6 text-brand-gold mx-auto mb-6" />
            <h1 className="font-cormorant text-[32px] md:text-[42px] text-brand-ink mb-3">
              The Enquiry Ledger
            </h1>
            <p className="font-cormorant text-body-md text-deep-taupe mb-10">
              A private record of every note left at the House.
            </p>
            <form onSubmit={unlock} className="flex flex-col gap-6">
              <input
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Passcode"
                className="w-full bg-transparent border-0 border-b border-hairline-beige focus:border-brand-gold outline-none py-3 font-cormorant text-[18px] text-center text-brand-ink placeholder:text-outline-variant transition-colors"
                data-testid="enquiry-passcode-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="self-center inline-flex items-center gap-3 border border-brand-gold px-8 py-3 text-brand-ink hover:bg-brand-ink hover:text-ink-on-dark hover:border-brand-ink transition-all duration-500 disabled:opacity-60"
                data-testid="enquiry-unlock-button"
              >
                <span className="font-inter text-label-sm uppercase tracking-widest">
                  {loading ? "Opening…" : "Open Ledger"}
                </span>
                <ArrowRight className="w-4 h-4" />
              </button>
              {error && (
                <p className="font-inter text-label-sm text-error" data-testid="enquiry-error">
                  {error}
                </p>
              )}
            </form>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto">
            <div className="flex items-baseline justify-between border-b border-hairline-beige pb-6 mb-2">
              <h1 className="font-cormorant text-[32px] md:text-[42px] text-brand-ink">
                The Enquiry Ledger
              </h1>
              <span className="font-inter text-label-sm uppercase tracking-widest text-deep-taupe">
                {rows.length} {rows.length === 1 ? "Note" : "Notes"}
              </span>
            </div>
            {rows.length > 0 && (
              <div className="flex justify-end mb-8">
                <button
                  type="button"
                  onClick={downloadCsv}
                  className="inline-flex items-center gap-2 border border-hairline-beige px-5 py-2 text-brand-ink hover:border-brand-gold hover:text-brand-gold transition-all duration-500"
                  data-testid="enquiry-export-csv-button"
                >
                  <Download className="w-4 h-4" />
                  <span className="font-inter text-label-sm uppercase tracking-widest">Download CSV</span>
                </button>
              </div>
            )}
            {rows.length === 0 ? (
              <p className="font-cormorant text-body-lg text-deep-taupe py-16 text-center">
                No enquiries yet.
              </p>
            ) : (
              <div className="divide-y divide-hairline-beige">
                {rows.map((r) => (
                  <div key={r.id} className="py-8" data-testid="enquiry-row">
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                      <h2 className="font-cormorant text-[22px] text-brand-ink">
                        {r.name}
                        <span className="text-deep-taupe"> · {r.occasion}</span>
                      </h2>
                      <span className="font-inter text-xs uppercase tracking-widest text-brand-secondary">
                        {fmt(r.created_at)}
                      </span>
                    </div>
                    <p className="font-cormorant text-body-md text-deep-taupe mb-4 max-w-3xl">
                      {r.message}
                    </p>
                    <div className="flex flex-wrap gap-x-8 gap-y-1 font-inter text-xs uppercase tracking-widest text-brand-secondary">
                      {r.email && <span>Email · {r.email}</span>}
                      {r.phone && <span>Phone · {r.phone}</span>}
                      {r.event_date && <span>Date · {r.event_date}</span>}
                      {r.guests && <span>Guests · {r.guests}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Enquiries;
